import type { GetPaths, GetRequest, GetResult } from "api/types";

import {
  buildAnonymousUser,
  isAnonymousBrowsing,
  isAnonymousCheckout,
} from "./anonymousBrowsingState";

/**
 * The explicit anonymous-browsing contract for authenticated-only GETs.
 * `src/api/index.ts` `get()` answers these paths locally while
 * `isAnonymousBrowsing()` is true; the backend is never asked. Paths the
 * backend serves anonymously (platform, shop, listings, memberships, packages,
 * retail products, gift cards) are intentionally absent so they keep hitting
 * the real API. `/multi-platform/list` is `[Authorize]` on the backend, hence
 * stubbed.
 */

const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

type StubMap = { [P in GetPaths]?: (args: GetRequest<P>) => GetResult<P> | null };

const stubs: StubMap = {
  "/user": () => buildAnonymousUser(),
  "/user/preferences": () => ({ concernIds: [] }),
  "/auth/claims": () => ({
    claims: {
      platformUserId: ZERO_GUID,
      userId: ZERO_GUID,
      platformId: ZERO_GUID,
      adminUserId: null,
      phoneNumber: null,
      tokenVersion: null,
      adminRoles: 0,
    },
  }),
  "/multi-platform/list": () => ({ isGloballyBlocked: false, platformDomainTokens: [] }),

  "/rewards": () => [],
  "/rewards/referrable": () => [],
  "/promotions": () => [],
  "/promotions/{promotionId}": () => null,
  // The offer detail renders nothing until this resolves, so a stub is what keeps the page from
  // hanging on a blank offer while checkout is off.
  "/promotions/{promotionId}/inclusions": () => null,
  "/promotions/active-banners": () => [],
  "/promotions/active-popups": () => [],
  "/visit/count": () => ({ totalVisits: 0 }),
  // `true` keeps the rewards banner on its call-to-action button (which becomes
  // "Log in") instead of the "you've checked in today" confirmation.
  "/user/check-in": () => true,
  "/incentives/{platformLocationId}/available-referral-reward": () => null,
  "/incentives/{platformLocationId}/active-signup-reward": () => null,
  "/incentives/{platformId}/referral-history/{personId}": () => ({
    referralHistory: [],
    specifiedLanguageCode: 0,
  }),
  "/incentives/{platformId}/person/{personId}/unacknowledged": () => ({
    awardedReferralRewards: [],
    bonusAwardedReferralRewards: [],
    specifiedLanguageCode: 0,
  }),
  "/referral-rewards/{platformId}/location/{locationId}": () => [],
  "/gift-cards/transfers/not-acknowledged": () => [],

  "/platform-user-memberships": () => [],
  "/platform-user-memberships/status": () => [],
  "/platform-user-packages": () => [],
  "/retailproducts/{platformId}/orders": () => [],
  "/retailproducts/{platformId}/earned": () => [],
  "/orders/current/details": () => null,
  "/orders/current/summary": () => null,
  "/orders/current/lineitems": () => [],

  "/adonis/top-recommendations": () => [],
  "/adonis/top-recommendations/id": () => [],
  "/ageless/capabilities": () => ({
    isEnabled: false,
    hasAesthetic: false,
    hasWellness: false,
    flowUrl: null,
    showOnPatientHome: false,
    isSubscriptionPaused: false,
  }),

  "/shop/discounts": () => ({ listingDiscounts: {}, membershipDiscounts: {} }),
  "/shop/discounts/listings/{listingId}": () => ({ packageDiscounts: {} }),
  "/memberships/discount-prices": () => [],
  "/retailproducts/storefront-settings": () => ({
    locationIdsThatSellProducts: [],
    refundEligibilityDays: 0,
    supportEmailAddresses: [],
    returnPolicy: "",
  }),
  "/platform/locations": () => ({ items: [], page: 1, totalPages: 0, count: 0, pageSize: 0 }),
};

export const ANONYMOUS_STUBBED_PATHS = Object.keys(stubs) as GetPaths[];

/**
 * Pricing the backend serves anonymously once the practice turns anonymous
 * checkout on. They stay in `stubs` (and therefore in
 * `ANONYMOUS_STUBBED_PATHS`) so a mode flip still purges their cached answers;
 * `resolveAnonymousGet` skips the stub while checkout is on.
 *
 * The promotion reads are here rather than in `ANONYMOUS_BROWSE_PATHS` because
 * an offer is pricing: the backend gates them on the same
 * `AllowAnonymousBrowsing(RequireCheckoutEnabled = true)`, and answers with the
 * public promotions advertised at the browsed location. Member-gated offers are
 * withheld server-side by `AnonymousPromotionPolicy`, so nothing a visitor sees
 * here is one they would lose on verifying.
 */
const ANONYMOUS_CHECKOUT_PRICING_PATHS = new Set<GetPaths>([
  "/shop/discounts",
  "/shop/discounts/listings/{listingId}",
  "/memberships/discount-prices",
  "/promotions",
  "/promotions/{promotionId}",
  "/promotions/{promotionId}/inclusions",
  "/promotions/active-banners",
  "/promotions/active-popups",
]);

/**
 * Served to any anonymous visitor, no checkout toggle required — the backend gates
 * `GET /platform/locations` on anonymous browsing alone. Which location a visitor browses decides which items are
 * listed at all, not just what they cost, so the location switcher has to work wherever browsing does.
 */
const ANONYMOUS_BROWSE_PATHS = new Set<GetPaths>(["/platform/locations"]);

export const getAnonymousStub = <P extends GetPaths>(path: P): StubMap[P] => stubs[path];

/** `undefined` means "not stubbed: perform the real request". */
export const resolveAnonymousGet = <P extends GetPaths>(
  path: P,
  args: GetRequest<P> | undefined,
): { value: GetResult<P> | null } | undefined => {
  if (!isAnonymousBrowsing()) return undefined;
  if (ANONYMOUS_BROWSE_PATHS.has(path)) return undefined;
  if (isAnonymousCheckout() && ANONYMOUS_CHECKOUT_PRICING_PATHS.has(path)) return undefined;
  const stub = getAnonymousStub(path);
  if (!stub) return undefined;
  return { value: stub((args ?? {}) as GetRequest<P>) };
};
